import React, { ReactNode, InputHTMLAttributes, SelectHTMLAttributes } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

/**
 * Shared form primitives for admin + merchant form pages and modals.
 * Single source of truth for input styling — keeps every form on-theme
 * and dark-mode-ready. See DESIGN_RULES.md → "Form pages / UiFormField".
 */

export const FIELD_INPUT_CLASS =
  'w-full px-3.5 py-2.5 border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-800 dark:text-slate-200 rounded-lg text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500 placeholder:text-gray-400 dark:placeholder:text-slate-500';

export const FIELD_LABEL_CLASS =
  'block text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1.5';

interface FieldBaseProps {
  label?: ReactNode;
  /** Icon shown inside the label */
  icon?: IconDefinition;
  required?: boolean;
  /** Helper text under the field */
  hint?: string;
  /** Error message — overrides hint and turns border red */
  error?: string;
  /** Wrapper className (e.g. grid spans) */
  wrapperClassName?: string;
}

const FieldShell: React.FC<FieldBaseProps & { htmlFor?: string; children: ReactNode }> = ({
  label, icon, required, hint, error, wrapperClassName = '', htmlFor, children,
}) => (
  <div className={wrapperClassName}>
    {label && (
      <label htmlFor={htmlFor} className={FIELD_LABEL_CLASS}>
        {icon && <FontAwesomeIcon icon={icon} className="mr-1.5 text-gray-400 dark:text-slate-500" />}
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
    )}
    {children}
    {error ? (
      <p className="mt-1 text-xs text-red-500 dark:text-red-400">{error}</p>
    ) : hint ? (
      <p className="mt-1 text-xs text-gray-400 dark:text-slate-500">{hint}</p>
    ) : null}
  </div>
);

const errorBorder = (error?: string) =>
  error ? 'border-red-300 dark:border-red-700 focus:border-red-400 focus:ring-red-100 dark:focus:ring-red-900/40' : '';

/* ── Input ────────────────────────────────────────────────────── */
interface UiInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'>, FieldBaseProps {
  /** Content rendered inside the left of the field (e.g. ฿) */
  prefix?: ReactNode;
}

export const UiInput: React.FC<UiInputProps> = ({
  label, icon, required, hint, error, wrapperClassName, prefix, className = '', id, ...rest
}) => (
  <FieldShell label={label} icon={icon} required={required} hint={hint} error={error} wrapperClassName={wrapperClassName} htmlFor={id}>
    <div className="relative">
      {prefix && (
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 font-semibold pointer-events-none">
          {prefix}
        </span>
      )}
      <input
        id={id}
        className={`${FIELD_INPUT_CLASS} ${prefix ? 'pl-9' : ''} ${errorBorder(error)} ${className}`}
        {...rest}
      />
    </div>
  </FieldShell>
);

/* ── Select ───────────────────────────────────────────────────── */
interface UiSelectProps extends SelectHTMLAttributes<HTMLSelectElement>, FieldBaseProps {
  children: ReactNode;
}

export const UiSelect: React.FC<UiSelectProps> = ({
  label, icon, required, hint, error, wrapperClassName, className = '', id, children, ...rest
}) => (
  <FieldShell label={label} icon={icon} required={required} hint={hint} error={error} wrapperClassName={wrapperClassName} htmlFor={id}>
    <select id={id} className={`${FIELD_INPUT_CLASS} ${errorBorder(error)} ${className}`} {...rest}>
      {children}
    </select>
  </FieldShell>
);
